import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, Badge } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useDateFormat } from "@/lib/dateFormat";
import { DatePicker } from "@/components/ui/date-picker";
import {
  BarChart3, CalendarDays, Download, FileSpreadsheet, FileText,
  Loader2, LogIn, LogOut, UserX, XCircle, DollarSign, BedDouble,
  Users, Clock, AlertTriangle, Timer, CheckCircle2, MoreVertical, Lock } from "lucide-react";

const REPORTS = [
  {
    key: "occupancy",
    title: "Occupancy & Reservations",
    desc: "Arrivals, departures, no-shows and cancellations for the selected period.",
    icon: CalendarDays,
  },
  {
    key: "revenue",
    title: "Revenue Summary",
    desc: "Daily & monthly revenue, broken down by room, for the selected period.",
    icon: BarChart3,
  },
  {
    key: "blocks",
    title: "Room Blocks & Lost Revenue",
    desc: "Blocked room-nights, reasons and the room revenue displaced for the selected period.",
    icon: Lock,
  },
  {
    key: "attendance",
    title: "Staff Attendance & Shifts",
    desc: "Scheduled shifts, clock-ins, late arrivals, hours worked and overtime alerts.",
    icon: Users,
  },
];

function firstOfMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function DataTable({ columns, rows, empty }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-neutral-800/60">
      <table className="w-full text-sm">
        <thead className="bg-neutral-900/50 text-neutral-400 text-[11px] uppercase tracking-wide">
          <tr>{columns.map((c) => <th key={c} className="text-left px-3 py-2 whitespace-nowrap">{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-neutral-800/50">
              {r.map((cell, j) => <td key={j} className="px-3 py-2 whitespace-nowrap">{cell}</td>)}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={columns.length} className="text-center py-8 text-neutral-500">{empty || "No records for this period"}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone = "info" }) {
  return (
    <Card className="p-4 flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-neutral-800/70 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-neutral-300" />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wide text-neutral-500">{label}</div>
        <div className="text-xl font-semibold mono">{value}</div>
      </div>
    </Card>
  );
}

export default function Reports() {
  const [tab, setTab] = useState("occupancy");
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(today());
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [data, setData] = useState(null);

  const active = REPORTS.find((r) => r.key === tab);

  const validRange = () => {
    if (!start || !end) { toast.error("Choose a start and end date"); return false; }
    if (end < start) { toast.error("End date must be on or after start date"); return false; }
    return true;
  };

  const generate = async () => {
    if (!validRange()) return;
    setLoading(true);
    setData(null);
    try {
      const q = new URLSearchParams({ start, end, format: "json" });
      const res = await api.get(`/reports/${tab}?${q.toString()}`);
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };

  const download = async (fmt) => {
    if (!validRange()) return;
    setDownloading(fmt);
    try {
      const q = new URLSearchParams({ start, end, format: fmt });
      const res = await api.get(`/reports/${tab}?${q.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${tab}_${start}_${end}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setDownloading("");
  };

  const switchTab = (k) => { setTab(k); setData(null); };

  return (
    <div className="space-y-5">
      <PageHeader title="Reports" subtitle="Generate and download operational reports for any date range." />

      {/* Report selector */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {REPORTS.map((r) => {
          const Icon = r.icon;
          const on = r.key === tab;
          return (
            <button
              key={r.key}
              onClick={() => switchTab(r.key)}
              data-testid={`report-tab-${r.key}`}
              className={`text-left p-4 rounded-xl border transition ${
                on ? "border-emerald-500/60 bg-emerald-500/5 ring-1 ring-emerald-500/30"
                   : "border-neutral-800 bg-neutral-900/30 hover:border-neutral-700"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`w-4 h-4 ${on ? "text-emerald-400" : "text-neutral-400"}`} />
                <span className="font-semibold">{r.title}</span>
              </div>
              <div className="text-[12px] text-neutral-500">{r.desc}</div>
            </button>
          );
        })}
      </div>

      {/* Date range + actions */}
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label htmlFor="rep-start">Start date</Label>
            <DatePicker value={start} maxDate={end || undefined} clearable={false}
                   onChange={(v) => setStart(v)} triggerClassName="w-full sm:w-52" data-testid="report-start" />
          </div>
          <div>
            <Label htmlFor="rep-end">End date</Label>
            <DatePicker value={end} minDate={start || undefined} clearable={false}
                   onChange={(v) => setEnd(v)} triggerClassName="w-full sm:w-52" data-testid="report-end" />
          </div>
          <Button onClick={generate} disabled={loading} data-testid="report-generate">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
            Generate
          </Button>
          <div className="flex-1" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="md:hidden w-11 px-0" aria-label="More" data-testid="report-more"><MoreVertical size={20} strokeWidth={1.5} /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="md:hidden">
              <DropdownMenuItem onClick={() => download("pdf")} disabled={!!downloading} data-testid="report-more-pdf"><FileText size={16} strokeWidth={1.5} className="mr-2" />Download PDF</DropdownMenuItem>
              <DropdownMenuItem onClick={() => download("xlsx")} disabled={!!downloading} data-testid="report-more-xlsx"><FileSpreadsheet size={16} strokeWidth={1.5} className="mr-2" />Download Excel</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" className="hidden md:inline-flex" onClick={() => download("pdf")} disabled={!!downloading} data-testid="report-download-pdf">
            {downloading === "pdf" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
            Download PDF
          </Button>
          <Button variant="outline" className="hidden md:inline-flex" onClick={() => download("xlsx")} disabled={!!downloading} data-testid="report-download-xlsx">
            {downloading === "xlsx" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1" />}
            Download Excel
          </Button>
        </div>
        <div className="text-[11px] text-neutral-500 mt-2">
          Showing <span className="text-neutral-300">{active.title}</span>. Downloads use the same date range.
        </div>
      </Card>

      {/* Preview */}
      {!data && !loading && (
        <Card className="p-10 text-center text-neutral-500">
          <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-40" />
          Choose a date range and click <span className="text-neutral-300">Generate</span> to preview the report,
          or download it directly as PDF / Excel.
        </Card>
      )}

      {loading && (
        <Card className="p-10 text-center text-neutral-500">
          <Loader2 className="w-6 h-6 mx-auto animate-spin" />
        </Card>
      )}

      {data && tab === "occupancy" && <OccupancyView data={data} />}
      {data && tab === "revenue" && <RevenueView data={data} />}
      {data && tab === "blocks" && <BlocksView data={data} />}
      {data && tab === "attendance" && <AttendanceView data={data} />}
    </div>
  );
}

function OccupancyView({ data }) {
  const s = data.summary;
  const { formatDate } = useDateFormat();
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 text-[12px] text-neutral-400">
        <Badge tone="info">{formatDate(data.period.start)} → {formatDate(data.period.end)}</Badge>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={LogIn} label="Arrivals" value={s.arrivals} />
        <Stat icon={LogOut} label="Departures" value={s.departures} />
        <Stat icon={UserX} label="No-shows" value={s.no_shows} />
        <Stat icon={XCircle} label="Cancellations" value={s.cancellations} />
      </div>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-neutral-300">Arrivals</h3>
        <DataTable columns={["Code", "Guest", "Room Type", "Arrival", "Departure", "Status", "Source"]}
          rows={data.arrivals.map((x) => [x.code, x.guest, x.room_type, formatDate(x.arrival), formatDate(x.departure),
            <Badge key="s" tone="info">{x.status}</Badge>, x.source])} />
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-neutral-300">Departures</h3>
        <DataTable columns={["Code", "Guest", "Expected Departure", "Status", "Balance"]}
          rows={data.departures.map((x) => [x.code, x.guest, formatDate(x.departure),
            <Badge key="s" tone="info">{x.status}</Badge>, x.balance])} />
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-neutral-300">No-shows</h3>
        <DataTable columns={["Code", "Guest", "Arrival", "Source"]}
          rows={data.no_shows.map((x) => [x.code, x.guest, formatDate(x.arrival), x.source])} />
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-neutral-300">Cancellations</h3>
        <DataTable columns={["Code", "Guest", "Arrival", "Cancelled On", "Reason"]}
          rows={data.cancellations.map((x) => [x.code, x.guest, formatDate(x.arrival), formatDate(x.cancelled_at), x.reason])} />
      </section>
    </div>
  );
}

function RevenueView({ data }) {
  const s = data.summary;
  const cur = data.currency;
  const centres = data.centres;
  const labels = data.centre_labels;
  const { formatDate } = useDateFormat();
  const money = (v) => `${cur} ${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 text-[12px] text-neutral-400">
        <Badge tone="info">{formatDate(data.period.start)} → {formatDate(data.period.end)}</Badge>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={DollarSign} label={`Total Revenue (${cur})`} value={money(s.total_revenue)} />
        <Stat icon={CalendarDays} label="Days w/ Revenue" value={s.days_with_revenue} />
        <Stat icon={BarChart3} label="Months" value={s.months} />
        <Stat icon={BedDouble} label="Rooms" value={s.rooms} />
      </div>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-neutral-300">Daily Revenue</h3>
        <DataTable
          columns={["Date", ...centres.map((c) => labels[c]), "Total"]}
          rows={data.daily.map((r) => [formatDate(r.date), ...centres.map((c) => money(r[c] ?? 0)), <span key="t" className="font-semibold">{money(r.total)}</span>])}
          empty="No revenue posted in this period" />
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-neutral-300">Monthly Revenue</h3>
        <DataTable columns={["Month", "Total"]}
          rows={data.monthly.map((r) => [r.month, money(r.total)])}
          empty="No revenue posted in this period" />
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-neutral-300">Revenue by Room</h3>
        <DataTable columns={["Room", "Revenue"]}
          rows={data.by_room.map((r) => [r.room, money(r.revenue)])}
          empty="No revenue posted in this period" />
      </section>

      <section className="space-y-2" data-testid="revenue-by-source">
        <h3 className="text-sm font-semibold text-neutral-300">Bookings &amp; Revenue by Source</h3>
        <DataTable columns={["Source", "Bookings", "Nights", "Room Revenue"]}
          rows={(data.by_source || []).map((r) => [r.source, r.bookings, r.nights, <span key="v" className="font-semibold">{money(r.revenue)}</span>])}
          empty="No bookings arriving in this period" />
      </section>
    </div>
  );
}

const BLOCK_TONE = { active: "neutral", scheduled: "info", completed: "success", cancelled: "error" };
function BlocksView({ data }) {
  const s = data.summary;
  const { formatDate } = useDateFormat();
  const money = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div className="space-y-5" data-testid="blocks-report-view">
      <div className="flex items-center gap-2 text-[12px] text-neutral-400">
        <Badge tone="info">{formatDate(data.period.start)} → {formatDate(data.period.end)}</Badge>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Lock} label="Blocks" value={s.total_blocks} />
        <Stat icon={BedDouble} label="Blocked Room-Nights" value={s.blocked_room_nights} />
        <Stat icon={DollarSign} label="Lost Revenue" value={money(s.lost_revenue)} />
        <Stat icon={AlertTriangle} label="Rooms Affected" value={s.rooms_affected} />
      </div>
      <section className="space-y-2" data-testid="blocks-by-reason">
        <h3 className="text-sm font-semibold text-neutral-300">Lost Revenue by Reason</h3>
        <DataTable columns={["Reason", "Blocks", "Blocked Nights", "Lost Revenue"]}
          rows={data.by_reason.map((r) => [r.reason, r.blocks, r.nights, <span key="v" className="font-semibold">{money(r.lost_revenue)}</span>])}
          empty="No room blocks in this period" />
      </section>
      <section className="space-y-2" data-testid="blocks-by-room">
        <h3 className="text-sm font-semibold text-neutral-300">Lost Revenue by Room</h3>
        <DataTable columns={["Room", "Blocks", "Blocked Nights", "Lost Revenue"]}
          rows={data.by_room.map((r) => [r.room, r.blocks, r.nights, money(r.lost_revenue)])}
          empty="No room blocks in this period" />
      </section>
      <section className="space-y-2" data-testid="blocks-detail">
        <h3 className="text-sm font-semibold text-neutral-300">Block Detail</h3>
        <DataTable columns={["Room", "From", "To", "Nights in Period", "Reason", "Status", "Blocked By", "Lost Revenue"]}
          rows={data.blocks.map((b) => [b.room, formatDate(b.block_from), formatDate(b.block_to), b.nights_in_range,
            `${b.reason}${b.reason_note ? ` — ${b.reason_note}` : ""}`, <Badge key="s" tone={BLOCK_TONE[b.status] || "neutral"}>{b.status}</Badge>, b.blocked_by || "—", money(b.lost_revenue)])}
          empty="No room blocks in this period" />
      </section>
    </div>
  );
}

const ATT_TONE = {
  present: "success", late: "warning", absent: "error",
  on_leave: "info", scheduled: "neutral",
};
const ATT_LABEL = {
  present: "Present", late: "Late", absent: "Absent",
  on_leave: "On Leave", scheduled: "Scheduled",
};

function AttendanceView({ data }) {
  const s = data.summary;
  const { formatDate } = useDateFormat();
  return (
    <div className="space-y-5" data-testid="attendance-report-view">
      <div className="flex items-center gap-2 text-[12px] text-neutral-400">
        <Badge tone="info">{formatDate(data.period.start)} → {formatDate(data.period.end)}</Badge>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Users} label="Staff" value={s.staff} />
        <Stat icon={CalendarDays} label="Shifts Scheduled" value={s.shifts_scheduled} />
        <Stat icon={CheckCircle2} label="Present" value={s.present} />
        <Stat icon={Clock} label="Late" value={s.late} />
        <Stat icon={UserX} label="Absent" value={s.absent} />
        <Stat icon={LogOut} label="On Leave" value={s.on_leave} />
        <Stat icon={Timer} label="Total Hours" value={s.total_hours} />
        <Stat icon={AlertTriangle} label="Overtime Hours" value={s.overtime_hours} />
      </div>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-neutral-300">Attendance by Employee</h3>
        <DataTable
          columns={["Employee", "Department", "Scheduled", "Present", "Late", "Absent", "On Leave", "Total Hrs", "Overtime Hrs"]}
          rows={data.by_employee.map((x) => [x.employee_name, x.department, x.scheduled, x.present,
            x.late, x.absent, x.on_leave, x.total_hours, x.overtime_hours])}
          empty="No attendance in this period" />
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-neutral-300">Daily Attendance Detail</h3>
        <DataTable
          columns={["Date", "Employee", "Department", "Shift", "Status", "In", "Out", "Hrs"]}
          rows={data.detail.map((x) => [formatDate(x.date), x.employee, x.department, x.shift,
            <Badge key="s" tone={ATT_TONE[x.status] || "neutral"}>{ATT_LABEL[x.status] || x.status}</Badge>,
            x.clock_in || "—", x.clock_out || "—", x.hours || "—"])}
          empty="No shifts or attendance in this period" />
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-neutral-300">Late & Overtime Alerts</h3>
        <DataTable
          columns={["Date", "Type", "Employee", "Department", "Message", "Status"]}
          rows={data.alerts.map((x) => [formatDate(x.date),
            <Badge key="t" tone={x.type === "overtime" ? "info" : "warning"}>{x.type}</Badge>,
            x.employee, x.department, x.message,
            <Badge key="st" tone={x.status === "Open" ? "warning" : "neutral"}>{x.status}</Badge>])}
          empty="No late or overtime alerts in this period" />
      </section>
    </div>
  );
}
